import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Input, Label, Row } from "reactstrap";

import { PaginateQuery } from "../../interfaces";
import { EducationalStageDto } from "../../interfaces/curriculum";
import { SelectListDto } from "../../models";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { useGetCurriculumSelectListQuery } from "../../store/api/curriculumApi";
import { useGetEducationSystemSelectListQuery } from "../../store/api/educationSystemApi";
import { useGetEducationalStagesQuery } from "../../store/api/educationalStageApi";
import { useGetSubjectsSelectListByGradeQuery } from "../../store/api/subjectApi";

export type AssignedSubjectOption = {
  value: string;
  label: string;
};

type TProps = {
  value: AssignedSubjectOption[];
  onChange: (subjects: AssignedSubjectOption[]) => void;
  disabled?: boolean;
};

const labelOf = (
  item: { value?: { en?: string; ar?: string }; name?: { en?: string; ar?: string } } | undefined,
  langKey: "en" | "ar",
) => item?.value?.[langKey] || item?.value?.en || item?.name?.[langKey] || item?.name?.en || "";

export const TeacherSubjectAssignment: React.FC<TProps> = ({
  value = [],
  onChange,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [stageId, setStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [subjectId, setSubjectId] = useState("");

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery();

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId,
      courseId,
    };
    return q;
  }, [curriculumId, courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !curriculumId || !courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];

  const selectedStage = useMemo(
    () => stages.find((s) => s.id === stageId),
    [stages, stageId],
  );
  const grades = selectedStage?.grades || [];

  const { data: subjects = [] } = useGetSubjectsSelectListByGradeQuery(
    gradeId,
    { skip: !gradeId },
  );

  useEffect(() => {
    if (!educationSystems?.length) return;
    if (
      educationSystemId &&
      educationSystems.some((s: any) => s.id === educationSystemId)
    ) {
      return;
    }
    setEducationSystemId(educationSystems[0].id);
  }, [educationSystems, educationSystemId]);

  useEffect(() => {
    if (!curricula?.length) {
      if (curriculumId) setCurriculumId("");
      return;
    }
    if (curriculumId && curricula.some((c: any) => c.id === curriculumId)) {
      return;
    }
    setCurriculumId(curricula[0].id);
  }, [curricula, curriculumId]);

  useEffect(() => {
    if (!courses?.length) return;
    if (courseId && courses.some((c: any) => c.id === courseId)) return;
    setCourseId(courses[0].id);
  }, [courses, courseId]);

  useEffect(() => {
    if (!stages.length) {
      if (stageId) setStageId("");
      return;
    }
    if (stageId && stages.some((s) => s.id === stageId)) return;
    setStageId(stages[0].id);
  }, [stages, stageId]);

  useEffect(() => {
    if (!grades.length) {
      if (gradeId) setGradeId("");
      return;
    }
    if (gradeId && grades.some((g) => g.id === gradeId)) return;
    setGradeId(grades[0].id);
  }, [grades, gradeId]);

  useEffect(() => {
    setSubjectId("");
  }, [gradeId]);

  const selectedEducationSystem = educationSystems?.find(
    (s: any) => s.id === educationSystemId,
  );
  const selectedCurriculum = curricula?.find((c: any) => c.id === curriculumId);
  const selectedCourse = courses?.find((c: any) => c.id === courseId);
  const selectedGrade = grades.find((g) => g.id === gradeId);
  const selectedSubject = subjects.find((s: SelectListDto) => s.id === subjectId);

  const buildSubjectLabel = (subject: SelectListDto) => {
    const parts = [
      labelOf(selectedEducationSystem, langKey),
      labelOf(selectedCurriculum, langKey),
      labelOf(selectedCourse, langKey),
      selectedStage?.name?.[langKey] || selectedStage?.name?.en,
      selectedGrade?.name?.[langKey] || selectedGrade?.name?.en,
      labelOf(subject, langKey),
    ].filter(Boolean);
    return parts.join(" / ");
  };

  const parseAssignedLabel = (label: string) => {
    const parts = label
      .split(/\s*\/\s*|\s*-\s*/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      return {
        name: parts[parts.length - 1],
        path: parts.slice(0, -1).join(" / "),
      };
    }
    return { name: label, path: "" };
  };

  const onAddSubject = () => {
    if (!selectedSubject) return;
    if (value.some((s) => s.value === selectedSubject.id)) return;

    onChange([
      ...value,
      {
        value: selectedSubject.id,
        label: buildSubjectLabel(selectedSubject),
      },
    ]);
    setSubjectId("");
  };

  const onRemoveSubject = (id: string) => {
    onChange(value.filter((s) => s.value !== id));
  };

  const canAdd =
    !!subjectId &&
    !disabled &&
    !value.some((s) => s.value === subjectId);

  return (
    <div className="teacher-subject-assignment">
      <Row className="g-2 mb-2">
        <Col md={4}>
          <Label className="form-label">{t("educationSystem")}</Label>
          <Input
            type="select"
            value={educationSystemId}
            disabled={disabled}
            onChange={(e) => {
              setEducationSystemId(e.target.value);
              setCurriculumId("");
              setStageId("");
              setGradeId("");
              setSubjectId("");
            }}
          >
            {(educationSystems || []).map((s: any) => (
              <option key={s.id} value={s.id}>
                {labelOf(s, langKey)}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={4}>
          <Label className="form-label">{t("curriculumItem")}</Label>
          <Input
            type="select"
            value={curriculumId}
            disabled={disabled || !educationSystemId}
            onChange={(e) => {
              setCurriculumId(e.target.value);
              setStageId("");
              setGradeId("");
              setSubjectId("");
            }}
          >
            {!curricula?.length ? (
              <option value="">{t("selectCurriculum")}</option>
            ) : null}
            {(curricula || []).map((c: any) => (
              <option key={c.id} value={c.id}>
                {labelOf(c, langKey)}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={4}>
          <Label className="form-label">{t("course")}</Label>
          <Input
            type="select"
            value={courseId}
            disabled={disabled}
            onChange={(e) => {
              setCourseId(e.target.value);
              setStageId("");
              setGradeId("");
              setSubjectId("");
            }}
          >
            {(courses || []).map((c: any) => (
              <option key={c.id} value={c.id}>
                {labelOf(c, langKey)}
              </option>
            ))}
          </Input>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md={4}>
          <Label className="form-label">{t("educationalStages")}</Label>
          <Input
            type="select"
            value={stageId}
            disabled={disabled || !curriculumId || !courseId}
            onChange={(e) => {
              setStageId(e.target.value);
              setGradeId("");
              setSubjectId("");
            }}
          >
            {!stages.length ? (
              <option value="">{t("selectEducationalStage")}</option>
            ) : null}
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name[langKey] || stage.name.en}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={4}>
          <Label className="form-label">{t("grade")}</Label>
          <Input
            type="select"
            value={gradeId}
            disabled={disabled || !stageId}
            onChange={(e) => {
              setGradeId(e.target.value);
              setSubjectId("");
            }}
          >
            {!grades.length ? (
              <option value="">{t("selectGrade")}</option>
            ) : null}
            {grades.map((grade) => (
              <option key={grade.id} value={grade.id}>
                {grade.name[langKey] || grade.name.en}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={4}>
          <Label className="form-label">{t("subject")}</Label>
          <div className="d-flex gap-2">
            <Input
              type="select"
              value={subjectId}
              disabled={disabled || !gradeId}
              onChange={(e) => setSubjectId(e.target.value)}
            >
              <option value="">{t("selectSubject")}</option>
              {subjects
                .filter(
                  (s: SelectListDto) =>
                    !value.some((assigned) => assigned.value === s.id),
                )
                .map((s: SelectListDto) => (
                  <option key={s.id} value={s.id}>
                    {labelOf(s, langKey)}
                  </option>
                ))}
            </Input>
            <Button
              color="success"
              outline
              className="flex-shrink-0"
              disabled={!canAdd}
              onClick={onAddSubject}
            >
              <i className="ri-add-line me-1"></i>
              {t("add")}
            </Button>
          </div>
        </Col>
      </Row>

      {(selectedEducationSystem || selectedCurriculum) && (
        <small className="text-muted d-block mb-2">
          {[
            labelOf(selectedEducationSystem, langKey),
            labelOf(selectedCurriculum, langKey),
            labelOf(selectedCourse, langKey),
            selectedStage?.name?.[langKey] || selectedStage?.name?.en,
            selectedGrade?.name?.[langKey] || selectedGrade?.name?.en,
          ]
            .filter(Boolean)
            .join(" / ")}
        </small>
      )}

      <div className="mt-2">
        <Label className="form-label mb-2">
          {t("assignedSubjects")}
          <span className="text-danger">*</span>
          <span className="text-info ms-1">({t("chooseAtLeast1Subject")})</span>
        </Label>
        {value.length === 0 ? (
          <div className="text-muted small border rounded p-3">
            {t("noSubjectsAssigned")}
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {value.map((subject) => {
              const { name, path } = parseAssignedLabel(subject.label);
              return (
                <div
                  key={subject.value}
                  className="d-flex align-items-start justify-content-between gap-2 border rounded px-3 py-2 bg-light"
                >
                  <div className="min-w-0">
                    <div className="fw-semibold text-body">{name}</div>
                    {path ? (
                      <small className="text-muted d-block text-wrap">
                        {path}
                      </small>
                    ) : null}
                  </div>
                  {!disabled ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-soft-danger flex-shrink-0"
                      aria-label={t("remove")}
                      onClick={() => onRemoveSubject(subject.value)}
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
